import { Component, ElementRef, inject, Input, OnInit, ViewChild } from '@angular/core';
import { SbModalState } from '../modal-state.service';
import { FreetextFilteredOptions } from '../modal.model';
import { API } from 'src/app/services/api/api';
import { of, Observable } from 'rxjs';
import { map, debounceTime, distinctUntilChanged, tap, switchMap, catchError } from 'rxjs/operators';
import { CullAtomAutocomplete } from 'src/app/components/culls/CullAtomAutoComplete';
import { UtilService } from '../../../util.service';
import { TranslateService } from '@ngx-translate/core';
import { StringTransformationService } from '../../../transformation-helper/string-transformation.service';

@Component({
  selector: 'sb-freetext-filtered-modal',
  templateUrl: './sb-freetext-filtered-modal.component.html',
  styleUrls: ['./sb-freetext-filtered-modal.component.scss'],
  standalone: false,
})
export class SbFreetextFilteredModalComponent implements OnInit {
  private readonly stringTransformation = inject(StringTransformationService);

  @ViewChild('inputElementElse') inputElementElse: ElementRef;
  @ViewChild('inputElementIf') inputElementIf: ElementRef;

  options: FreetextFilteredOptions<string>;
  @Input()
  templateId: string;
  selectedOption: any;

  datatype: string;
  attributeType: string;

  cullAtomAutocomplete: CullAtomAutocomplete;

  searching = false;
  searchFailed = false;

  // tslint:disable-next-line:variable-name
  _empty: boolean;

  get empty(): boolean {
    return this._empty;
  }

  set empty(val: boolean) {
    this._empty = val;
    if (this._empty) {
      this.selectedOption = null;
    }
  }

  namedFormatter = (x: { name: string }) => x.name;

  ngOnInit() {
    this.options = this.state.options.get(this.templateId) as FreetextFilteredOptions<string>;
    this.selectedOption = this.options.initial;
    if (this.selectedOption === '') {
      this.empty = true;
    }
    this.datatype = this.options.datatype;
    this.attributeType = this.options.attributeType;
    this.cullAtomAutocomplete = this.options.cullAtomAutoComplete;
  }

  constructor(
    private state: SbModalState,
    private api: API,
    public util: UtilService,
    public translate: TranslateService
  ) {}

  ok() {
    if (this.checkOkEnabled()) {
      this.state.modals.get(this.templateId).close({
        value: this.selectedOption,
        empty: this.empty,
        cancel: false,
      });
    }
  }

  enterpress() {
    if (this.checkOkEnabled()) {
      this.ok();
    }
  }

  checkOkEnabled() {
    return true;
    /* TODO check here for need
    switch (this.attributeType) {
      case "POOL":
        if (this.selectedOption) {
          return this.selectedOption !== null;
        }
        return false;
      default:
        return true;
    }*/
  }

  checkShowDelete() {
    if (!this.selectedOption) {
      return false;
    } else {
      switch (this.attributeType) {
        case 'PROPERTY':
        case 'LOCATION':
        case 'CHANNEL':
        case 'POOL':
          return true;
        default:
          return this.selectedOption?.length > 0;
      }
    }
  }

  clear() {
    this.selectedOption = null;
  }

  getSelectedOptionText() {
    if (this.empty) {
      return this.translate.instant('lang.globals.empty');
    }
    switch (this.attributeType) {
      // case 'PROPERTY':
      case 'LOCATION':
      case 'CHANNEL':
      case 'POOL':
        return this.selectedOption ? this.selectedOption.name : null;
      default:
        return this.selectedOption;
    }
  }

  cancel() {
    this.state.modals.get(this.templateId).close({ value: null, cancel: true });
  }

  search(search: string): Observable<any> {
    if (search.charAt(0) !== '%') {
      search = '%' + search;
    }
    if (this.attributeType) {
      switch (this.attributeType) {
        // case 'PROPERTY':
        case 'LOCATION':
        case 'CHANNEL':
        case 'POOL':
          break;
        default:
          if (search === '') {
            return of([]);
          }
      }
      switch (this.attributeType) {
        /*case 'PROPERTY':

            break;*/
        case 'LOCATION':
          return this.api
            .ss_fm_Location_retrieveAll({
              search: this.stringTransformation.encodeSearchQueryParam(false, search),
            })
            .pipe(
              map(response => {
                return response;
              })
            );
        case 'CHANNEL':
          return this.api
            .ss_nm_Channel_retrieveAll({
              search: this.stringTransformation.encodeSearchQueryParam(false, search),
            })
            .pipe(
              map(response => {
                return response;
              })
            );
        case 'POOL':
          return this.api
            .ss_nm_Pool_retrieveAll({
              search: this.stringTransformation.encodeSearchQueryParam(false, search),
            })
            .pipe(
              map(response => {
                return response;
              })
            );
        case 'LOCATION_ATTRIBUTE':
          return this.api
            .ca_md_LocationAttribute_retrieveValuesByDatatypeId(this.datatype, {
              search: this.stringTransformation.encodeSearchQueryParam(false, search),
            })
            .pipe(
              map(response => {
                return response;
              })
            );
        case 'DEVICE_ATTRIBUTE':
          return this.api
            .ca_md_DeviceAttribute_retrieveValuesByDatatypeId(this.datatype, {
              search: this.stringTransformation.encodeSearchQueryParam(false, search),
            })
            .pipe(
              map(response => {
                return response;
              })
            );
        case 'COMPANY_ATTRIBUTE':
          return this.api
            .ca_md_CompanyAttribute_retrieveValuesByDatatypeId(this.datatype, {
              search: this.stringTransformation.encodeSearchQueryParam(false, search),
            })
            .pipe(
              map(response => {
                return response;
              })
            );
        default:
          return of([]);
      }
    } else if (this.cullAtomAutocomplete) {
      return this.api
        .cs_Cull_freeTextAutocomplete(this.cullAtomAutocomplete.autocomplete, this.cullAtomAutocomplete.cullAtom, {
          search: this.stringTransformation.encodeSearchQueryParam(false, search),
        })
        .pipe(
          map(response => {
            return response;
          })
        );
    }
  }

  searchTermAsResult(term) {
    return this.search(term).pipe(
      tap(response => {
        response.unshift(term);
      })
    );
  }

  dosearch = (text$: Observable<string>) =>
    text$.pipe(
      debounceTime(300),
      distinctUntilChanged(),
      tap(() => (this.searching = true)),
      switchMap(term =>
        this.searchTermAsResult(term).pipe(
          tap(() => (this.searchFailed = false)),
          catchError(() => {
            this.searchFailed = true;
            return of([]);
          })
        )
      ),
      tap(() => (this.searching = false))
    );

  openSuggestions() {
    if (
      this.attributeType === 'POOL' ||
      this.attributeType === 'LOCATION' ||
      this.attributeType === 'CHANNEL' ||
      // || this.attributeType === 'PROPERTY'
      this.cullAtomAutocomplete
    ) {
      this.inputElementIf.nativeElement.value = '';
      this.inputElementIf.nativeElement.dispatchEvent(new Event('input'));
      this.inputElementIf.nativeElement.focus();
    } else {
      this.inputElementElse.nativeElement.value = '';
      this.inputElementElse.nativeElement.dispatchEvent(new Event('input'));
      this.inputElementElse.nativeElement.focus();
    }
  }
}
